import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { AssessmentHistory } from "./tabs/assessmentHistoryTab";
import RiskParameterTab from "./tabs/riskParameterTab";

import { useGetSystemConditionAndRiskById } from "@features/ax/assetRegistry/facilities/hooks";
import { useAssessCalculation } from "@features/ax/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { OverviewTab } from "./tabs/overviewTab";

export const ConditionAndRiskLanding: React.FC<{ networkData?: any }> = ({
  networkData,
}) => {
  const { id } = useParams<{ id: string }>();

  const remoteUtilityId = useRemoteUtilityId();
  const assessmentName = "network-assessment";
  const { data: conditionAndRiskData } = useGetSystemConditionAndRiskById(
    assessmentName,
    Number(id)
  );
  const assessCondition = useAssessCalculation(assessmentName, Number(id));
  const assessRisk = useAssessCalculation(assessmentName, Number(id));
  useEffect(() => {
    logEvent("conditions_tab_viewed");
  }, []);

  const handleAssessCondition = () => {
    const payload = {
      isAccessCondition: true,
      entityType: 5,
      entityId: Number(id),
      remoteUtilityId: Number(remoteUtilityId),
    };
    assessCondition.mutateAsync(payload);
  };

  const handleAssessRisk = () => {
    const payload = {
      isAccessRisk: true,
      entityType: 5,
      entityId: Number(id),
      remoteUtilityId: Number(remoteUtilityId),
    };
    assessRisk.mutateAsync(payload);
  };

  const urlMapping = {
    overview: "overview",
    riskParameter: "risk-parameter",
    assessmentHistory: "assessment-history",
  };

  const tabComponents = {
    overview: {
      label: "Overview",
      shortLabel: "Overview",
      component: <OverviewTab data={conditionAndRiskData} />,
    },
    riskParameter: {
      label: "Critical Parameters",
      shortLabel: "Critical Parameter",
      component: <RiskParameterTab data={networkData} />,
    },
    assessmentHistory: {
      label: "Assessment History",
      shortLabel: "Assessment History",
      component: <AssessmentHistory />,
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold">
                Network Condition
              </CardTitle>
              <Button
                isLoading={assessCondition.isPending}
                variant="outline"
                onClick={() => handleAssessCondition()}
              >
                Assess Condition
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold text-gray-900">
                {conditionAndRiskData?.lastConditionAssessment
                  ?.conditionScore || 0}
              </div>
              <Badge>
                {conditionAndRiskData?.lastConditionAssessment
                  ?.conditionRating || "N/A"}
              </Badge>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                Last Assessed:{" "}
                {conditionAndRiskData?.lastConditionAssessment?.createdDate ||
                  "N/A"}
              </p>
              <p>
                Assessed By:{" "}
                {conditionAndRiskData?.lastConditionAssessment
                  ?.createdUserRemoteName || "N/A"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold">
                Network Risk
              </CardTitle>
              <Button
                isLoading={assessRisk.isPending}
                variant="outline"
                onClick={() => handleAssessRisk()}
              >
                Assess Risk
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold text-gray-900">
                {conditionAndRiskData?.lastRiskAssessment?.riskScore || 0}
              </div>
              <Badge>
                {conditionAndRiskData?.lastRiskAssessment?.riskRating || "N/A"}
              </Badge>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                Last Assessed:{" "}
                {conditionAndRiskData?.lastRiskAssessment?.createdDate || "N/A"}
              </p>
              <p>
                Assessed By:{" "}
                {conditionAndRiskData?.lastRiskAssessment
                  ?.createdUserRemoteName || "N/A"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      <Tabs
        defaultValue="overview"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        tabsListClassName="mt-4 grid w-full grid-cols-3 mb-6"
        idPrefix="condition-and-risk-tab"
        level={1}
      />
    </div>
  );
};
